import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    deletePracticeDocument,
    fetchPracticeDocuments,
    uploadPracticeDocument,
    type UploadPracticeDocumentInput
} from '../services/practiceDocuments';
import { PRACTICE_DOCUMENTS_KEY } from './queryKeys';

export function usePracticeDocuments(practiceId: string | undefined) {
    const query = useQuery({
        queryKey: PRACTICE_DOCUMENTS_KEY(practiceId ?? ''),
        queryFn: () => fetchPracticeDocuments(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60_000
    });

    return {
        documents: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch
    };
}

interface UploadPracticeDocumentArgs {
    input: UploadPracticeDocumentInput;
    onProgress?: (percent: number) => void;
}

export function useUploadPracticeDocument(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ input, onProgress }: UploadPracticeDocumentArgs) =>
            uploadPracticeDocument(practiceId, input, onProgress),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_DOCUMENTS_KEY(practiceId) });
        }
    });
}

export function useDeletePracticeDocument(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (practiceFileId: string) => deletePracticeDocument(practiceId, practiceFileId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_DOCUMENTS_KEY(practiceId) });
        }
    });
}
